/**
 * Which columns a tabular dataset's rows have, and what to call them.
 *
 * Two surfaces render a tabular dataset's records now — the dataset viewer and
 * a collection whose underlying dataset is tabular — so the derivation lives
 * here rather than inline in the viewer. It is not obvious enough to
 * reimplement: the registered field list is authoritative when it exists, and
 * the fallback reads the keys off the records themselves, which is the only
 * thing that works for a dataset whose fields were never registered.
 *
 * `prettifyColumnName` had six byte-identical private copies. The viewer's
 * table now imports this one, so the viewer and the Library agree on a header;
 * the five under `products/equipment-ai/` are left where they are rather than
 * migrated by a Library change.
 */

const columnNameCache = new Map<string, string>();

/**
 * `unit__max_temp_dtm` → `Unit > Max Temp Date/Time`.
 *
 * Falls back to the raw name when prettification produces nothing to show —
 * a field named `_` (seen in the wild) reduces to a lone space.
 */
export const prettifyColumnName = (name: string): string => {
  const cached = columnNameCache.get(name);
  if (cached) return cached;
  const pretty = name
    // Salesforce's custom-field suffix, not this convention's nested-path
    // separator — stripped first, so "Contract_Length__c" reads as "Contract
    // Length" rather than the nested-path reading "Contract Length > C".
    .replace(/__c$/i, "")
    .replace(/__/g, " > ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/Dtm$/i, "Date/Time")
    .replace(/^Is /i, "")
    .trim();
  const result = pretty || name;
  columnNameCache.set(name, result);
  return result;
};

/** The subset of a dataset field this derivation needs. */
export interface ColumnField {
  field_name: string;
}

/** The subset of a record this derivation needs. */
export interface ColumnRecord {
  data?: Record<string, unknown> | null;
}

/**
 * True for a name that can actually become a column.
 *
 * Deliberately not a check against Name/Owner/Modified/Size: this derivation
 * is shared with the dataset viewer's own tabular table, which has none of
 * those as columns of its own to collide with.
 */
const isUsableColumnName = (name: string): boolean => name.trim() !== "";

/** Alphabetical, case-insensitively — `.sort()`'s default is code-unit order, which puts every capital ahead of every lowercase letter and reads as unsorted the moment cases mix. */
const byName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

const NESTED_METADATA_KEYS = new Set(["extracted_metadata", "source_metadata"]);

/**
 * Column names for a tabular dataset, in display order.
 *
 * The registered fields win when there are any. Otherwise the keys are read
 * off the loaded records, so a dataset nobody registered fields for still
 * renders columns — that fallback is why this is worth sharing rather than
 * each caller mapping `field_name` and calling it done. Both branches sort
 * alphabetically: the registered list is API/registration order, which
 * carries no promised meaning and reads as arbitrary once a dataset has more
 * than a handful of fields (frontend#2530).
 *
 * `extracted_metadata` and `source_metadata` are excluded: they are nested
 * document metadata that happen to live in `data`, not columns of the row.
 */
export const deriveDataColumns = (
  fields: ColumnField[] | undefined,
  records: ColumnRecord[] = [],
): string[] => {
  if (fields && fields.length > 0) {
    return fields
      .map((field) => field.field_name)
      .filter(isUsableColumnName)
      .sort(byName);
  }
  const keys = new Set<string>();
  records.forEach((record) => {
    if (record.data && typeof record.data === "object") {
      Object.keys(record.data).forEach((key) => {
        if (!NESTED_METADATA_KEYS.has(key) && isUsableColumnName(key)) {
          keys.add(key);
        }
      });
    }
  });
  return Array.from(keys).sort(byName);
};
